import { LktIconName, LktNotificationTone } from "@frontends-v3/design-system";

import {
  NotificationDto,
  NotificationSeverity
} from "../../../models/notifications/notification.interface";
import { resolveNotificationTarget } from "../../../utils/notification-target.util";

/**
 * Apresentação de uma notificação (DEV-872, tipos em DEV-895).
 *
 * O backend já entrega a frase montada (`subject` + `message` + `body`) e já resolve `icon` e
 * `severity` pelo catálogo. Logo este mapa carrega SÓ o que o backend não sabe: qual traço SVG do
 * design-system corresponde a cada nome de ícone do catálogo.
 *
 * PONTO DE EXTENSÃO: um tipo novo ACRESCENTA uma linha em `ICON_BY_CATALOG_NAME` quando traz um
 * nome de ícone inédito, e — se tiver ação contextual — uma entrada no mapa de destinos em
 * `notification-target.util.ts`. O `ctaLabel` NÃO se declara aqui de propósito: ele vem do destino,
 * para que rótulo e navegação não possam divergir.
 */
export interface NotificationPresentation {
  readonly icon: LktIconName;
  readonly tone: LktNotificationTone;
  /** Rótulo da ação contextual. Ausente = sem ação, e a linha renderiza sem botão. */
  readonly ctaLabel?: string;
}

/**
 * Nome de ícone do catálogo do backend → ícone do design-system.
 *
 * Os nomes são os que o catálogo REALMENTE emite (`NotificationTypeDomain.Icon`), não os do
 * protótipo de design: a etapa 1 semeou `coautor-entrou`/`atribuidor-saiu` para a tela nascer
 * pronta, e o catálogo da etapa 2 (DEV-895) entrou com `attributor-joined`/`attributor-left`. Os
 * traços apontados aqui são exatamente os do protótipo — `person` é a cabeça-e-ombros da entrada e
 * `sign-out` é a porta com seta da saída (`icon.component.html:226` e `:237`).
 */
const ICON_BY_CATALOG_NAME: Readonly<Record<string, LktIconName>> = {
  "system-diagnostic": "bell",
  "attributor-joined": "person",
  "attributor-left": "sign-out"
};

const TONE_BY_SEVERITY: Readonly<Record<NotificationSeverity, LktNotificationTone>> = {
  Info: "neutral",
  Warning: "warning",
  Critical: "negative"
};

const FALLBACK: NotificationPresentation = { icon: "bell", tone: "neutral" };

/**
 * Nunca devolve null: tipo desconhecido renderiza, com ícone e tom genéricos, sem CTA e sem
 * navegação. É o oposto do `buildActivityFeedEntry` do feed de atividade, que devolve null e omite
 * a linha — lá o front escreve a copy, aqui o backend escreve.
 *
 * O tom da saída de atribuidor sai `neutral` como o da entrada, e isso é intencional: a severidade
 * dos dois é `Info` no catálogo justamente para que a saída não seja pintada de alarme.
 */
export function resolveNotificationPresentation(item: NotificationDto): NotificationPresentation {
  const fromCatalog = item.icon ? ICON_BY_CATALOG_NAME[item.icon] : undefined;
  const fromSeverity = item.severity ? TONE_BY_SEVERITY[item.severity] : undefined;
  const target = resolveNotificationTarget(item);

  return {
    ...FALLBACK,
    ...(fromCatalog ? { icon: fromCatalog } : {}),
    ...(fromSeverity ? { tone: fromSeverity } : {}),
    ...(target ? { ctaLabel: target.ctaLabel } : {})
  };
}
